// AgentService源码统一命名；历史wire版本与字段保留，避免旧会话失配。
/** 订单客服三端共同使用的正式契约版本。 */
export const AGENT_SERVICE_CONTRACT_VERSION = "customer-service.v1";

/** Vue端正式Fixture必须匹配的Canonical内容摘要。 */
export const AGENT_SERVICE_FIXTURE_SHA256 =
  "9662e2c936f96d49be224c508e71085b5922edcee5878363e4443eba7133186b";

const ACTOR_TYPES = new Set([
  "CUSTOMER",
  "BUSINESS_STAFF",
  "AI_AGENT",
  "SYSTEM",
]);
const CONTROL_STATES = new Set([
  "AI_ENABLED",
  "WAITING_HUMAN",
  "HUMAN_ACTIVE",
  "CLOSED",
]);
const CUSTOMER_PARTIES = new Set(["BUYER", "SELLER", "VISITOR"]);
const ASSIGNED_STAFF_ROLES = new Set(["NEGOTIATOR", "BUSINESS", "SUPPORT", "PUBLISHER"]);
const ASSIGNMENT_SOURCES = new Set([
  "SUPPORT_CONFIG",
  "CLUE_PUBLISHER",
  "SHOP_CONTACTS",
  "ORDER_BUYER_NEGOTIATOR",
  "ORDER_SELLER_NEGOTIATOR",
  "ORDER_BUYER_BUSINESS",
  "ORDER_SELLER_BUSINESS",
]);
const CONTROL_ACTOR_TYPES = new Set(["CUSTOMER", "BUSINESS_STAFF", "SYSTEM"]);
const WEB_SOCKET_EVENT_TYPES = new Set([
  "customer.message.created.v1",
  "customer.conversation.control.changed.v1",
  "customer.handoff.requested.v1",
  "customer.agent.processing.v1",
  "customer.agent.failed.v1",
]);
const NULLABLE_MESSAGE_IDENTIFIER_FIELDS = new Set([
  "actorUserId",
  "clientMessageId",
  "deliveryId",
  "sourceEventId",
  "agentRunId",
  "profileReleaseId",
]);

const isRecord = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const requireRecord = (value, path) => {
  if (!isRecord(value)) {
    throw new TypeError(`${path}必须是对象`);
  }
  return value;
};

const requireString = (value, path) => {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new TypeError(`${path}必须是非空字符串`);
  }
  return value;
};

const requireMatchingString = (value, path, pattern) => {
  const text = requireString(value, path);
  if (!pattern.test(text)) {
    throw new TypeError(`${path}格式无效`);
  }
  return text;
};

const requireSetMember = (value, path, allowed) => {
  if (!allowed.has(value)) {
    throw new TypeError(`${path}取值无效：${String(value)}`);
  }
  return value;
};

const requirePositiveSafeInteger = (value, path) => {
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new TypeError(`${path}必须是正安全整数`);
  }
  return value;
};

const requireNonnegativeSafeInteger = (value, path) => {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new TypeError(`${path}必须是非负安全整数`);
  }
  return value;
};

const assertContractVersion = (value, path) => {
  const record = requireRecord(value, path);
  if (record.schemaVersion !== 1) {
    throw new TypeError(`${path}.schemaVersion必须为1`);
  }
  if (record.contractVersion !== AGENT_SERVICE_CONTRACT_VERSION) {
    throw new TypeError(
      `${path}.contractVersion必须为${AGENT_SERVICE_CONTRACT_VERSION}`
    );
  }
  return record;
};

const assertIdentifierFields = (
  value,
  path = "payload",
  nullableIdentifierFields = new Set()
) => {
  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      assertIdentifierFields(
        item,
        `${path}[${index}]`,
        nullableIdentifierFields
      )
    );
    return;
  }
  if (!isRecord(value)) return;

  Object.entries(value).forEach(([key, fieldValue]) => {
    const fieldPath = `${path}.${key}`;
    if (key.endsWith("Ids")) {
      if (
        !Array.isArray(fieldValue) ||
        fieldValue.some(
          (item) => typeof item !== "string" || item.trim().length === 0
        )
      ) {
        throw new TypeError(`${key}必须是非空字符串数组`);
      }
      return;
    }
    if (key.endsWith("Id")) {
      // Java DTO会把当前消息不适用的可选身份字段序列化为null；
      // 只允许消息契约列出的字段为空，其他业务ID仍须是非空字符串。
      if (fieldValue === null && nullableIdentifierFields.has(key)) return;
      requireString(fieldValue, key);
      return;
    }
    assertIdentifierFields(fieldValue, fieldPath, nullableIdentifierFields);
  });
};

const hasAgentIdentity = (message) =>
  Boolean(message.deliveryId || message.agentRunId || message.profileReleaseId);

/** 将AI消息末尾的推荐问句转为展示选项，兼容旧正文且不修改持久消息事实。 */
export function splitAgentServiceSuggestions(
  message,
  expectedQuestions = 0
) {
  const content = String(message?.content || "");
  if (message?.actorType !== "AI_AGENT") return { content, questions: [] };
  // 新译文以同一持久字段保存正文和有序按钮文本；标记不进入气泡、不改变历史语言快照。
  const marker = "\n\n[XSOU_SUGGESTIONS_V1]\n";
  const markerAt = content.lastIndexOf(marker);
  if (markerAt >= 0) {
    try {
      const items = JSON.parse(content.slice(markerAt + marker.length));
      if (
        Array.isArray(items) &&
        items.length >= 1 &&
        items.length <= 6 &&
        items.every(
          (item) =>
            typeof item === "string" && item.trim() && item.length <= 240
        )
      )
        return { content: content.slice(0, markerAt).trim(), questions: items };
    } catch {
      /* 非完整协议保留原文，不静默截断。 */
    }
  }
  // 仅在原AI消息确有对应建议时，兼容旧译文文末的日语/英语引号列表。
  // 不重译/回写历史，也不把普通回答中的引号当作按钮。
  if (expectedQuestions >= 2 && expectedQuestions <= 6) {
    const parts = content.split(/\n\s*\n/u);
    const tail = parts[parts.length - 1];
    const pattern =
      /(?:「([^」\n]{1,240}[？?])」|“([^”\n]{1,240}[？?])”|"([^"\n]{1,240}[？?])")/gu;
    const matches = [...tail.matchAll(pattern)];
    if (
      matches.length === expectedQuestions &&
      tail.replace(pattern, "").length < 100
    ) {
      parts.pop();
      if (
        parts.length &&
        /^[^\n]{1,160}[：:]\s*$/u.test(parts[parts.length - 1])
      )
        parts.pop();
      return {
        content: parts.join("\n\n").trim(),
        questions: matches.map((item) => item[1] || item[2] || item[3]),
      };
    }
  }
  const questions = [];
  const paragraphs = [];
  let displayContent = content;
  // 总客服可能用自然语言标题而非协议标签；只提取文末的完整问句列表，
  // 不把答案中引用的功能名称、操作说明或真人正文误当成推荐问题。
  const headings = content.matchAll(/^[^\n]{1,160}[：:][ \t]*$/gmu);
  for (const heading of headings) {
    const options = content.slice(heading.index + heading[0].length).trim();
    const lines = options
      .split(/\n/u)
      .map((line) => line.trim())
      .filter(Boolean);
    const matches = lines.map((line) =>
      line
        .replace(/^(?:[-*•]|\d+[.、)])\s*/u, "")
        .match(
          /^(?:“([^”\n]{1,240}[？?])”|"([^"\n]{1,240}[？?])"|「([^」\n]{1,240}[？?])」|([^“”"「」\n]{1,240}[？?]))[。；;，,、]*$/u
        )
    );
    // 保留无法完整识别的原始回复，避免截掉列表后的真实答案或说明。
    if (lines.length < 2 || lines.length > 6 || matches.some((match) => !match))
      continue;
    for (const match of matches) {
      const question = (match[1] || match[2] || match[3] || match[4]).trim();
      if (!questions.includes(question)) questions.push(question);
    }
    displayContent = content.slice(0, heading.index).trim();
    break;
  }
  for (const paragraph of displayContent.split(/\n\s*\n/u)) {
    const isLegacy =
      /^这个问题稍微偏离(?:了)?当前场景/u.test(paragraph) &&
      paragraph.includes("您可以问我：");
    const isSuggestion = /^可继续询问：/u.test(paragraph);
    if (!isLegacy && !isSuggestion) {
      paragraphs.push(paragraph.replace(/^场景回引：/u, ""));
      continue;
    }
    const options = paragraph.slice(paragraph.indexOf("：") + 1);
    for (const match of options.matchAll(/“([^”\n]{1,120})”/gu)) {
      const question = match[1].trim();
      if (question && !questions.includes(question) && questions.length < 6)
        questions.push(question);
    }
    if (isLegacy) {
      const guide = paragraph.replace(/，您可以问我：[\s\S]*$/u, "。");
      if (!paragraphs.includes(guide)) paragraphs.push(guide);
    }
  }
  return { content: paragraphs.join("\n\n").trim(), questions };
}

/**
 * 适配Spring返回的商品或订单锚点。
 * ID只能由JSON字符串进入页面，禁止先转Number再转回字符串。
 */
export function adaptAgentServiceScene(input) {
  const scene = requireRecord(input, "scene");
  assertIdentifierFields(scene, "scene");
  // GENERAL不能为了套用订单接口携带假商品或客服配置。
  if (scene.sceneType === "GENERAL" || scene.sceneType === "SUPPLY_DEMAND") {
    if (Object.keys(scene).some((key) => key !== "sceneType")) {
      throw new TypeError("GENERAL场景不能包含商品、订单或人员配置");
    }
    return { sceneType: scene.sceneType };
  }
  const goods = {
    goodsId: requireMatchingString(scene.goodsId, "goodsId", /^[1-9]\d*$/u),
    goodsCode: requireMatchingString(scene.goodsCode, "goodsCode", /^\d{18}$/u),
    partNumber: requireString(scene.partNumber, "partNumber"),
  };

  if (scene.sceneType === "PRODUCT") {
    if (scene.orderId !== undefined || scene.orderNum !== undefined) {
      throw new TypeError("PRODUCT场景不能包含订单身份");
    }
    return { sceneType: "PRODUCT", ...goods };
  }
  if (scene.sceneType === "ORDER") {
    return {
      sceneType: "ORDER",
      ...goods,
      orderId: requireMatchingString(scene.orderId, "orderId", /^[1-9]\d*$/u),
      orderNum: requireString(scene.orderNum, "orderNum"),
    };
  }
  throw new TypeError(`不支持的sceneType：${String(scene.sceneType)}`);
}

/**
 * 适配一条客户可见消息，并在进入Vue状态前核对AI与真人身份约束。
 */
export function adaptAgentServiceMessage(input) {
  const message = assertContractVersion(input, "message");
  assertIdentifierFields(
    message,
    "message",
    NULLABLE_MESSAGE_IDENTIFIER_FIELDS
  );
  requirePositiveSafeInteger(message.sequence, "sequence");

  if (!ACTOR_TYPES.has(message.actorType)) {
    throw new TypeError(`不支持的actorType：${String(message.actorType)}`);
  }
  requireString(message.businessMessageId, "businessMessageId");
  requireString(message.conversationId, "conversationId");
  requireString(message.displayNameSnapshot, "displayNameSnapshot");

  if (message.actorType === "AI_AGENT") {
    if (
      message.actorUserId ||
      message.messageOrigin !== "AGENT_DELIVERY" ||
      !message.deliveryId ||
      !message.agentRunId ||
      !message.profileReleaseId
    ) {
      throw new TypeError(
        "AI_AGENT消息必须包含deliveryId、agentRunId和profileReleaseId，且不能包含actorUserId"
      );
    }
  } else if (message.actorType === "BUSINESS_STAFF") {
    if (
      !message.actorUserId ||
      message.messageOrigin !== "STAFF_UI" ||
      hasAgentIdentity(message)
    ) {
      throw new TypeError(
        "BUSINESS_STAFF消息必须使用真人用户身份且不能携带Agent身份"
      );
    }
  } else if (message.actorType === "CUSTOMER") {
    if (
      !message.actorUserId ||
      message.messageOrigin !== "CUSTOMER_UI" ||
      hasAgentIdentity(message)
    ) {
      throw new TypeError(
        "CUSTOMER消息必须使用客户用户身份且不能携带Agent身份"
      );
    }
  } else if (
    message.actorUserId ||
    hasAgentIdentity(message) ||
    message.messageOrigin !== "SYSTEM"
  ) {
    throw new TypeError("SYSTEM消息不能携带用户或Agent身份");
  }

  return { ...message };
}

/** 根据服务端actorType生成稳定的页面身份，不按昵称或头像猜测。 */
export function resolveAgentServiceActorPresentation(input) {
  const message = adaptAgentServiceMessage(input);
  const kinds = {
    CUSTOMER: "CUSTOMER",
    BUSINESS_STAFF: "HUMAN_STAFF",
    AI_AGENT: "AI_ASSISTANT",
    SYSTEM: "SYSTEM",
  };
  return {
    kind: kinds[message.actorType],
    label: message.displayNameSnapshot,
  };
}

const adaptConversationView = (input) => {
  const conversation = assertContractVersion(input, "conversationView");
  assertIdentifierFields(conversation, "conversationView");
  if (!CONTROL_STATES.has(conversation.controlState)) {
    throw new TypeError(
      `不支持的controlState：${String(conversation.controlState)}`
    );
  }
  requirePositiveSafeInteger(conversation.controlVersion, "controlVersion");
  requireNonnegativeSafeInteger(conversation.lastSequence, "lastSequence");
  requireNonnegativeSafeInteger(
    conversation.customerLastReadSequence,
    "customerLastReadSequence"
  );
  requireNonnegativeSafeInteger(
    conversation.staffLastReadSequence,
    "staffLastReadSequence"
  );

  requireSetMember(
    conversation.customerParty,
    "customerParty",
    CUSTOMER_PARTIES
  );
  requireSetMember(
    conversation.assignedStaffRole,
    "assignedStaffRole",
    ASSIGNED_STAFF_ROLES
  );
  requireSetMember(
    conversation.assignmentSource,
    "assignmentSource",
    ASSIGNMENT_SOURCES
  );
  const scene = adaptAgentServiceScene(conversation.scene);
  if (scene.sceneType === "GENERAL") {
    if (
      conversation.customerParty !== "VISITOR" ||
      conversation.assignedStaffRole !== "SUPPORT" ||
      conversation.assignmentSource !== "SUPPORT_CONFIG"
    ) {
      throw new TypeError("GENERAL会话必须由服务端配置分配人工客服");
    }
  } else if (scene.sceneType === "SUPPLY_DEMAND") {
    if (conversation.customerParty !== "VISITOR" || conversation.assignedStaffRole !== "PUBLISHER"
      || conversation.assignmentSource !== "CLUE_PUBLISHER" || conversation.customerUserId === conversation.assignedStaffUserId)
      throw new TypeError("供需会话必须绑定不同的咨询人与实际发布人");
  } else if (
    conversation.assignedStaffRole === "PUBLISHER" || conversation.assignmentSource === "CLUE_PUBLISHER" ||
    conversation.customerParty === "VISITOR" ||
    conversation.assignedStaffRole === "SUPPORT" ||
    conversation.assignmentSource === "SUPPORT_CONFIG"
  ) {
    throw new TypeError("商品和订单会话不能借用总客服身份");
  }
  if (
    scene.sceneType === "PRODUCT" &&
    (conversation.customerParty !== "BUYER" ||
      conversation.assignedStaffRole !== "NEGOTIATOR" ||
      conversation.assignmentSource !== "SHOP_CONTACTS")
  ) {
    throw new TypeError("PRODUCT会话必须由买方客户分配给商品谈判人");
  }
  if (scene.sceneType === "ORDER") {
    // 客户买卖方与订单负责人字段必须同向，避免把咨询误派给交易另一方人员。
    const requiredSourcePrefix =
      conversation.customerParty === "BUYER" ? "ORDER_BUYER_" : "ORDER_SELLER_";
    if (!conversation.assignmentSource.startsWith(requiredSourcePrefix)) {
      throw new TypeError("ORDER会话负责人来源必须匹配客户买卖方");
    }
  }
  if (
    conversation.assignmentSource.endsWith("NEGOTIATOR") &&
    conversation.assignedStaffRole !== "NEGOTIATOR"
  ) {
    throw new TypeError("谈判人来源必须匹配NEGOTIATOR角色");
  }
  if (
    conversation.assignmentSource.endsWith("BUSINESS") &&
    conversation.assignedStaffRole !== "BUSINESS"
  ) {
    throw new TypeError("商务人员来源必须匹配BUSINESS角色");
  }
  if (
    conversation.customerLastReadSequence > conversation.lastSequence ||
    conversation.staffLastReadSequence > conversation.lastSequence
  ) {
    throw new TypeError("阅读序号不能超过会话最新序号");
  }
  return {
    ...conversation,
    scene,
  };
};

/** 适配Spring返回的正式会话视图，供Vue状态层在入库前统一校验。 */
export function adaptAgentServiceConversationView(input) {
  return adaptConversationView(input);
}

const adaptCustomerMessageCreatedEvent = (input) => {
  const event = assertContractVersion(input, "customerMessageCreatedEvent");
  assertIdentifierFields(event, "customerMessageCreatedEvent");
  requirePositiveSafeInteger(event.controlVersion, "controlVersion");
  requirePositiveSafeInteger(event.sequence, "sequence");
  adaptAgentServiceScene({
    sceneType: event.sceneType,
    goodsId: event.goodsId,
    goodsCode: event.goodsCode,
    partNumber: event.partNumber,
    ...(event.orderId
      ? { orderId: event.orderId, orderNum: event.orderNum }
      : {}),
  });
  return { ...event };
};

const adaptAgentServiceContext = (input) => {
  const context = assertContractVersion(input, "customerServiceContext");
  assertIdentifierFields(context, "customerServiceContext");
  requirePositiveSafeInteger(
    context.expectedControlVersion,
    "expectedControlVersion"
  );
  const conversation = adaptConversationView(context.conversation);
  const customerMessage = adaptAgentServiceMessage(context.customerMessage);
  const recentMessages = (context.recentMessages || []).map((message) =>
    adaptAgentServiceMessage(message)
  );
  const product = requireRecord(
    context.product,
    "customerServiceContext.product"
  );
  const productScene = adaptAgentServiceScene({
    sceneType: "PRODUCT",
    goodsId: product.goodsId,
    goodsCode: product.goodsCode,
    partNumber: product.partNumber,
  });
  const staffProfile = requireRecord(
    context.staffProfile,
    "customerServiceContext.staffProfile"
  );
  assertIdentifierFields(staffProfile, "customerServiceContext.staffProfile");

  if (
    context.tenantId !== conversation.tenantId ||
    context.tenantId !== customerMessage.tenantId ||
    context.businessMessageId !== customerMessage.businessMessageId ||
    customerMessage.conversationId !== conversation.conversationId ||
    customerMessage.actorType !== "CUSTOMER" ||
    customerMessage.actorUserId !== conversation.customerUserId ||
    context.expectedControlVersion !== conversation.controlVersion ||
    productScene.goodsId !== conversation.scene.goodsId ||
    productScene.goodsCode !== conversation.scene.goodsCode ||
    productScene.partNumber !== conversation.scene.partNumber ||
    staffProfile.assistantDisplayName !== conversation.assistantDisplayName
  ) {
    throw new TypeError("上下文消息、控制版本和业务锚点必须一致");
  }
  if (
    recentMessages.some(
      (message) =>
        message.tenantId !== context.tenantId ||
        message.conversationId !== conversation.conversationId ||
        (message.actorType === "CUSTOMER" &&
          message.actorUserId !== conversation.customerUserId) ||
        (message.actorType === "BUSINESS_STAFF" &&
          message.actorUserId !== conversation.assignedStaffUserId)
    )
  ) {
    throw new TypeError("历史消息必须属于当前会话及其冻结参与者");
  }
  if (
    conversation.scene.sceneType === "ORDER" &&
    (!isRecord(context.order) ||
      context.order.orderId !== conversation.scene.orderId ||
      context.order.orderNum !== conversation.scene.orderNum)
  ) {
    throw new TypeError("订单上下文必须包含一致的orderId和orderNum");
  }
  if (conversation.scene.sceneType === "PRODUCT" && context.order) {
    throw new TypeError("商品上下文不能包含订单数据");
  }

  return {
    ...context,
    conversation,
    customerMessage,
    recentMessages,
    product: { ...product },
    staffProfile: { ...staffProfile },
    ...(context.order ? { order: { ...context.order } } : {}),
  };
};

const adaptAgentDeliveryRequest = (input) => {
  const request = assertContractVersion(input, "agentDeliveryRequest");
  assertIdentifierFields(request, "agentDeliveryRequest");
  requirePositiveSafeInteger(
    request.expectedControlVersion,
    "expectedControlVersion"
  );
  if (
    !isRecord(request.delivery) ||
    !["AGENT_MESSAGE", "HANDOFF_REQUEST"].includes(request.delivery.kind)
  ) {
    throw new TypeError("Agent Delivery正文类型无效");
  }
  if (request.delivery.kind === "AGENT_MESSAGE") {
    requireString(request.delivery.content, "delivery.content");
  } else {
    requireString(request.delivery.reasonCode, "delivery.reasonCode");
    requireString(request.delivery.reason, "delivery.reason");
  }
  return { ...request, delivery: { ...request.delivery } };
};

const adaptAgentDeliveryReceipt = (input) => {
  const receipt = assertContractVersion(input, "agentDeliveryReceipt");
  assertIdentifierFields(receipt, "agentDeliveryReceipt");
  requirePositiveSafeInteger(
    receipt.currentControlVersion,
    "currentControlVersion"
  );
  requireSetMember(
    receipt.currentControlState,
    "currentControlState",
    CONTROL_STATES
  );
  if (["DELIVERED", "DUPLICATE"].includes(receipt.status)) {
    requireString(receipt.businessMessageId, "businessMessageId");
    requirePositiveSafeInteger(receipt.sequence, "sequence");
  } else if (["SUPPRESSED", "REJECTED"].includes(receipt.status)) {
    requireString(receipt.errorCode, "errorCode");
    requireString(receipt.reason, "reason");
    if (
      receipt.businessMessageId !== undefined ||
      receipt.sequence !== undefined
    ) {
      throw new TypeError("拒绝或抑制的Delivery回执不能携带业务消息身份");
    }
  } else {
    throw new TypeError(`不支持的Delivery回执状态：${String(receipt.status)}`);
  }
  return { ...receipt };
};

const adaptControlStateChangedEvent = (input) => {
  const event = assertContractVersion(input, "controlStateChangedEvent");
  assertIdentifierFields(event, "controlStateChangedEvent");
  requirePositiveSafeInteger(event.controlVersion, "controlVersion");
  if (!CONTROL_STATES.has(event.controlState)) {
    throw new TypeError(`不支持的controlState：${String(event.controlState)}`);
  }
  if (!CONTROL_STATES.has(event.previousControlState)) {
    throw new TypeError(
      `不支持的previousControlState：${String(event.previousControlState)}`
    );
  }
  if (event.previousControlState === event.controlState) {
    throw new TypeError("控制状态变化事件的新旧状态不能相同");
  }
  requireSetMember(
    event.changedByActorType,
    "changedByActorType",
    CONTROL_ACTOR_TYPES
  );
  if (event.changedByActorType === "SYSTEM" && event.changedByUserId) {
    throw new TypeError("SYSTEM控制变化不能携带用户ID");
  }
  if (event.changedByActorType !== "SYSTEM" && !event.changedByUserId) {
    throw new TypeError("客户或业务员控制变化必须携带用户ID");
  }
  return { ...event };
};

/**
 * 适配Spring推送给浏览器的五类正式WebSocket通知。
 * 未知事件不能进入页面状态，避免把内部Outbox事件直接当作浏览器协议。
 */
export function adaptAgentServiceWebSocketNotification(input) {
  const notification = assertContractVersion(input, "webSocketNotification");
  assertIdentifierFields(notification, "webSocketNotification");
  requireString(notification.eventId, "eventId");
  requireString(notification.tenantId, "tenantId");
  requireString(notification.conversationId, "conversationId");
  requireString(notification.occurredAt, "occurredAt");
  if (!WEB_SOCKET_EVENT_TYPES.has(notification.eventType)) {
    throw new TypeError(
      `不支持的WebSocket事件：${String(notification.eventType)}`
    );
  }

  if (notification.eventType === "customer.message.created.v1") {
    requireString(notification.businessMessageId, "businessMessageId");
    requirePositiveSafeInteger(notification.sequence, "sequence");
  } else if (
    notification.eventType === "customer.conversation.control.changed.v1"
  ) {
    requireSetMember(
      notification.previousControlState,
      "previousControlState",
      CONTROL_STATES
    );
    requireSetMember(notification.controlState, "controlState", CONTROL_STATES);
    requirePositiveSafeInteger(notification.controlVersion, "controlVersion");
    // AI->AI的版本失效通知用于“只取消本轮回答”，不伪装成人工接管或关闭会话。
    if (notification.previousControlState === notification.controlState && notification.controlState !== 'AI_ENABLED') {
      throw new TypeError("控制状态通知的新旧状态不能相同");
    }
  } else if (notification.eventType === "customer.handoff.requested.v1") {
    requirePositiveSafeInteger(notification.controlVersion, "controlVersion");
    requireSetMember(
      notification.requestedByActorType,
      "requestedByActorType",
      CONTROL_ACTOR_TYPES
    );
    requireString(notification.reason, "reason");
  } else if (notification.eventType === "customer.agent.failed.v1") {
    requireString(
      notification.sourceBusinessMessageId,
      "sourceBusinessMessageId"
    );
    requireString(notification.agentRunId, "agentRunId");
    const error = requireRecord(
      notification.error,
      "webSocketNotification.error"
    );
    requireString(error.code, "error.code");
    requireString(error.message, "error.message");
    if (typeof error.retryable !== "boolean") {
      throw new TypeError("error.retryable必须是布尔值");
    }
  } else if (notification.eventType === "customer.agent.processing.v1") {
    requireString(
      notification.sourceBusinessMessageId,
      "sourceBusinessMessageId"
    );
    requireString(notification.agentRunId, "agentRunId");
  }

  return {
    ...notification,
    ...(notification.error ? { error: { ...notification.error } } : {}),
  };
}

/**
 * 适配Spring与Agent Platform之间的非秘密机器身份元数据。
 * 服务身份与方向固定绑定，浏览器不得按传入值自行选择服务身份。
 */
export function adaptAgentServiceInternalRequestMetadata(input) {
  const metadata = assertContractVersion(input, "internalRequestMetadata");
  assertIdentifierFields(metadata, "internalRequestMetadata");
  requireString(metadata.tenantId, "tenantId");
  requireString(metadata.requestId, "requestId");
  requireString(metadata.serviceId, "serviceId");
  requireString(metadata.serviceVersion, "serviceVersion");
  if (Object.prototype.hasOwnProperty.call(metadata, "authorization")) {
    throw new TypeError("机器身份元数据不能携带authorization");
  }

  if (metadata.direction === "SPRING_TO_PLATFORM") {
    if (metadata.serviceId !== "xsou-da-customer-service") {
      throw new TypeError(
        "SPRING_TO_PLATFORM调用方必须为xsou-da-customer-service"
      );
    }
  } else if (metadata.direction === "PLATFORM_TO_SPRING") {
    if (metadata.serviceId !== "xsou-agent-platform-customer-service") {
      throw new TypeError(
        "PLATFORM_TO_SPRING调用方必须为xsou-agent-platform-customer-service"
      );
    }
  } else {
    throw new TypeError(`不支持的内部调用方向：${String(metadata.direction)}`);
  }

  return { ...metadata };
}

/**
 * 回放三端共用的customer-service.v1 Fixture。
 * 返回新对象，避免测试或页面逻辑反向修改冻结样本。
 */
export function adaptAgentServiceFixture(input) {
  const fixture = requireRecord(input, "fixture");
  assertIdentifierFields(fixture, "fixture");
  const handoffRequest = requireRecord(
    fixture.handoffRequest,
    "handoffRequest"
  );
  requirePositiveSafeInteger(
    handoffRequest.expectedControlVersion,
    "expectedControlVersion"
  );
  requireString(handoffRequest.clientRequestId, "clientRequestId");
  const webSocketNotifications = requireRecord(
    { items: fixture.webSocketNotifications },
    "webSocketNotifications"
  ).items;
  if (!Array.isArray(webSocketNotifications)) {
    throw new TypeError("webSocketNotifications必须是数组");
  }

  return {
    ...fixture,
    customerMessageCreatedEvent: adaptCustomerMessageCreatedEvent(
      fixture.customerMessageCreatedEvent
    ),
    orderCustomerMessageCreatedEvent: adaptCustomerMessageCreatedEvent(
      fixture.orderCustomerMessageCreatedEvent
    ),
    conversationView: adaptConversationView(fixture.conversationView),
    orderConversationView: adaptConversationView(fixture.orderConversationView),
    customerMessageView: adaptAgentServiceMessage(
      fixture.customerMessageView
    ),
    aiMessageView: adaptAgentServiceMessage(fixture.aiMessageView),
    customerServiceContext: adaptAgentServiceContext(
      fixture.customerServiceContext
    ),
    orderCustomerServiceContext: adaptAgentServiceContext(
      fixture.orderCustomerServiceContext
    ),
    agentDeliveryRequest: adaptAgentDeliveryRequest(
      fixture.agentDeliveryRequest
    ),
    agentDeliveryReceipt: adaptAgentDeliveryReceipt(
      fixture.agentDeliveryReceipt
    ),
    controlStateChangedEvent: adaptControlStateChangedEvent(
      fixture.controlStateChangedEvent
    ),
    handoffRequest: { ...handoffRequest },
    webSocketNotifications: webSocketNotifications.map((notification) =>
      adaptAgentServiceWebSocketNotification(notification)
    ),
    springToPlatformRequestMetadata:
      adaptAgentServiceInternalRequestMetadata(
        fixture.springToPlatformRequestMetadata
      ),
    platformToSpringRequestMetadata:
      adaptAgentServiceInternalRequestMetadata(
        fixture.platformToSpringRequestMetadata
      ),
    error: { ...requireRecord(fixture.error, "error") },
  };
}
